import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const TESTIMONIALS = [{
  quote: '“Por primera vez sentí que estaba siguiendo un plan pensado para esta etapa de mi vida.”',
  name: 'Laura',
  age: '52 años',
  country: 'Argentina'
}, {
  quote: '“Necesitaba una forma más simple de acompañar esta etapa sin exigirme como antes.”',
  name: 'María',
  age: '49 años',
  country: 'Argentina'
}, {
  quote: '“Sentí que por fin tenía un recorrido claro para seguir, sin volver a exigirme de la misma manera.”',
  name: 'Carolina',
  age: '47 años',
  country: 'Argentina'
}];
type Testimonial = (typeof TESTIMONIALS)[number];
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -10
  }} transition={{
    duration: 0.3,
    ease: 'easeOut'
  }} className="card-premium relative overflow-hidden p-7 md:p-9" data-id="element-401" data-mp="32gj07j7157mq">
      <div className="space-y-7" data-id="element-402" data-mp="34qujw427hsjv">
        {/* Stars */}
        <div className="flex gap-1" role="img" aria-label="Testimonio de alumna" data-id="element-403" data-mp="2zskktidd27sz">
          {[...Array(5)].map((_, index) => <StarIcon key={index} className="h-4 w-4 text-secondary fill-secondary" data-id="element-404" data-mp="2c4t31z91x2kb" data-mpi={index} />)}
        </div>

        {/* Quote */}
        <p className="font-body text-[17px] leading-[1.7] italic text-foreground/90 md:text-[19px]" data-id="element-405" data-mp="2m2h8bj4a9sgq">
          {testimonial.quote}
        </p>

        {/* Author */}
        <div className="border-t border-border/50 pt-5" data-id="element-406" data-mp="19vwbjzk3m0n">
          <p className="font-display text-lg font-bold text-gold-light" data-id="element-407" data-mp="1ci2pgapnvha">
            {testimonial.name}
          </p>

          <p className="mt-1 font-body text-sm text-foreground/55" data-id="element-408" data-mp="3f0wfq1466yl">
            {testimonial.age} · {testimonial.country}
          </p>
        </div>
      </div>
    </motion.div>;
}
export function TestimonialsSection() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const current = TESTIMONIALS[activeTestimonial];
  const previousTestimonial = () => {
    setActiveTestimonial(currentIndex => currentIndex === 0 ? TESTIMONIALS.length - 1 : currentIndex - 1);
  };
  const nextTestimonial = () => {
    setActiveTestimonial(currentIndex => currentIndex === TESTIMONIALS.length - 1 ? 0 : currentIndex + 1);
  };
  return <section className="bg-gradient-section px-4 py-12 md:px-8 md:py-16" data-id="element-409" data-mp="3vj34ccp5zke3">
      <div className="mx-auto max-w-[1400px] space-y-10 md:space-y-12" data-id="element-410" data-mp="3vkea2i26727u">

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="space-y-3 text-center" data-id="element-411" data-mp="2y1cs8gr0vzo1">
          <p className="text-secondary text-[10px] font-bold uppercase tracking-[0.24em] md:text-xs" data-id="element-412" data-mp="1b569q1mp5hs2">
            Lo que cuentan mujeres que hicieron H.E.R.
          </p>

          <h2 className="font-display text-3xl font-black leading-tight md:text-4xl" data-id="element-413" data-mp="118thz9appdzy">
            Experiencias{' '}
            <span className="text-gradient-gold" data-id="element-414" data-mp="wgiig7jvvu7x">Reales</span>
          </h2>
        </motion.div>

        {/* Testimonial */}
        <div className="mx-auto max-w-2xl" data-id="element-415" data-mp="sghplhw1fghc">
          <div className="relative" data-id="element-416" data-mp="nh55h4wjwnig">
            <AnimatePresence mode="wait" initial={false} data-id="element-417" data-mp="ni6hpcpazg1r">
              {current && <TestimonialCard key={`${current.name}-${activeTestimonial}`} testimonial={current} data-id="element-418" data-mp="igqvvifax5ib" />}
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="mt-6 flex items-center justify-center gap-4" data-id="element-419" data-mp="2dn1mxxzogi88">
            {/* Previous */}
            <button type="button" onClick={previousTestimonial} aria-label="Testimonio anterior" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C8A870]/30 text-[#C8A870] transition-all duration-300 hover:border-[#C8A870]/60 hover:bg-[#C8A870]/5" data-id="element-420" data-mp="2im4m51sz7u8a">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-421" data-mp="1zulh6pfe663m" />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2" role="tablist" aria-label="Seleccionar testimonio" data-id="element-422" data-mp="1kxcn3nypeo87">
              {TESTIMONIALS.map((testimonial, index) => <button key={testimonial.name} type="button" onClick={() => setActiveTestimonial(index)} role="tab" aria-selected={activeTestimonial === index} aria-label={`Ver testimonio ${index + 1}`} className={`rounded-full transition-all duration-300 ${index === activeTestimonial ? 'h-1.5 w-6 bg-[#C8A870]' : 'h-1.5 w-1.5 bg-[#C8A870]/25 hover:bg-[#C8A870]/50'}`} data-id="element-423" data-mp="3fau2gnoyges5" data-mpi={index} />)}
            </div>

            {/* Next */}
            <button type="button" onClick={nextTestimonial} aria-label="Siguiente testimonio" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C8A870]/30 text-[#C8A870] transition-all duration-300 hover:border-[#C8A870]/60 hover:bg-[#C8A870]/5" data-id="element-424" data-mp="1nbhhatr0vju1">
              <ChevronRightIcon className="h-4 w-4" data-id="element-425" data-mp="6wyd34vg94b8" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}